import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { isReadOnlyRole } from '../../utils/readOnlySeat';
import { DocEntriesBtn, type DocCtx } from './docEntries';
import type { InBooksRow } from '../../types';

/**
 * Batch 11 R3 (COA-D9, stage two; W2-13, design §3.5.5) — the staff way on from every refusal: "Reverse JV-…".
 *
 * ⛔ ONE REVERSE IMPLEMENTATION. This is not a second reversal dialog: it is the document's entries panel
 *    (docEntries.tsx DocEntriesBtn, batch 11 R8a), opened FOCUSED on the entry the refusal named — where the reason is
 *    written, the reversal posted and, for a document, the edit retried. `label` / `focusEntryId` are R8a's props
 *    (W2 brief §4.6), passed straight through (the W2 integration dropped the cast this file carried before R8a merged).
 * ⛔ STAFF WHO WRITE THE BOOKS ONLY. A read-only seat (books, auditor — isReadOnlyRole) and a login without the books
 *    module get nothing: the refusal's sentence stands alone. The client portal never renders this (its words never
 *    say "reverse").
 * ⛔ An entry whose reversal is already raised says "Post the reversal R…" — the same panel, the same entry: R is
 *    nested under it there with its own Post.
 */

export function ReverseEntryBtn({ ctx, kind, id, docNo, entry, style }: {
  ctx: DocCtx;
  /** The holding document's entries-for kind (its STORED doc_type, 'receipt', 'payable', 'bank_line', …). */
  kind: string; id: string; docNo: React.ReactNode;
  /** The entry the refusal named. */
  entry: Pick<InBooksRow, 'entryId' | 'docNo' | 'reversalDraft'>;
  style?: React.CSSProperties;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const writes = !!me && !isReadOnlyRole(me.role) && (me.modules || []).includes('accounting_books');
  if (!writes || !entry || !entry.entryId) return null;
  const label = entry.reversalDraft
    ? t('aw_ll_open_reversal', { rv: entry.reversalDraft })
    : t('aw_ll_reverse', { docNo: entry.docNo || entry.entryId });
  return (
    <span data-ll-reverse={entry.docNo || entry.entryId} style={{ display: 'inline-flex' }}>
      <DocEntriesBtn ctx={ctx} kind={kind} id={id} docNo={docNo} style={style} label={label} focusEntryId={entry.entryId} />
    </span>
  );
}

export default ReverseEntryBtn;
